import { apply, withTokenError, withTokenRejected } from '@lib/interceptors';
import { getAllToken } from '@util/token';
import axios, { InternalAxiosRequestConfig } from 'axios';

/**
 * 점주 백오피스 패스키(WebAuthn) API. (LETMEUP-403)
 * 백엔드 계약(LETMEUP-402): /v2/backoffice/passkey
 *  - POST /authenticate/begin  body { merchantCode }            → { sessionId, options }      (공개)
 *  - POST /authenticate/finish body { sessionId, response }     → { accessToken, refreshToken, loginType } (공개)
 *  - POST /register/begin                                       → { sessionId, options }      (JWT)
 *  - POST /register/finish     body { sessionId, response, deviceName? } → { passkey }         (JWT)
 *  - GET  /list                                                 → { passkeys[], optOut }       (JWT)
 *  - PATCH /opt-out            body { optOut }                  → { optOut }                   (JWT)
 *  - PATCH /:id                body { deviceName }              → { passkey }                  (JWT)
 *  - DELETE /:id                                                → { id, revokedAt }            (JWT)
 *
 * REACT_APP_API_URL_V2 는 `.../v2/backoffice` 까지 포함(기존 auth/seat 인스턴스와 동일 규약).
 */

const API_URL_V2 = process.env.REACT_APP_API_URL_V2;

const createInstance = (url?: string, timeout?: number) =>
  axios.create({ baseURL: `${API_URL_V2}${url}`, timeout: timeout || 30000 });

/**
 * 패스키 관리(등록/조회/opt-out/rename/revoke)용 인스턴스.
 * 로그인된 점주(merchantToken) 우선, 없으면 admin(adminToken) 토큰 주입.
 * 백엔드 jwtUserAuthAbove(jwt-auth-user 전략)가 양쪽 토큰을 모두 디코드한다.
 */
const withPasskeyTokenAccepted = (config: InternalAxiosRequestConfig<any>) => {
  const { merchantToken, adminToken } = getAllToken();
  const token = merchantToken || adminToken;
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
};

// 인증(비로그인) — 토큰 미주입
export const passkey = createInstance('/passkey');
// 관리(JWT) — 토큰 주입
const passkeyWithToken = createInstance('/passkey');

passkeyWithToken.interceptors.request.use(withPasskeyTokenAccepted, withTokenError);
passkeyWithToken.interceptors.response.use(apply, withTokenRejected);

export interface PasskeyItem {
  id: string;
  deviceName: string | null;
  transports: string[];
  backupEligible: boolean;
  backupState: boolean;
  usernamelessReady?: boolean;
  lastUsedAt: string | null;
  createdAt: string;
  credentialIdHash: string;
}

// ---- 인증 (공개) ----

/** 인증 begin: merchantCode 로 활성 credential allowList + challenge 발급 */
export const postAuthenticateBegin = (merchantCode: string) =>
  passkey.post('/authenticate/begin', { merchantCode });

/** 인증 finish: 서명 검증 성공 시 { accessToken, refreshToken, loginType } 발급 */
export const postAuthenticateFinish = (sessionId: string, response: any) =>
  passkey.post('/authenticate/finish', { sessionId, response });

// ---- 등록 / 관리 (JWT) ----

/** 등록 begin: 등록 옵션 + sessionId 반환 */
export const postRegisterBegin = () => passkeyWithToken.post('/register/begin');

/** 등록 finish: credential 저장 */
export const postRegisterFinish = (sessionId: string, response: any, deviceName?: string) =>
  passkeyWithToken.post('/register/finish', { sessionId, response, deviceName });

/** 본인 활성 패스키 목록 + opt-out 상태 */
export const getPasskeyList = () => passkeyWithToken.get('/list');

/** 등록 권유 모달 영구 거부 토글 */
export const patchOptOut = (optOut: boolean) => passkeyWithToken.patch('/opt-out', { optOut });

/** 패스키 별명 변경 */
export const patchPasskeyName = (id: string, deviceName: string) =>
  passkeyWithToken.patch(`/${id}`, { deviceName });

/** 패스키 폐기(soft revoke) */
export const deletePasskey = (id: string) => passkeyWithToken.delete(`/${id}`);
